/**
 * FCM 푸시 알림 초기화 유틸
 *
 * 현재: Firebase 패키지 미설치 상태 → 환경변수 설정 시 안내 메시지만 출력
 *
 * Firebase 활성화 방법:
 *   1. npm install firebase @capacitor-firebase/messaging
 *   2. .env에 REACT_APP_FIREBASE_* 환경변수 채우기
 *   3. initWebFCM(), initCapacitorFCM() 주석 해제
 *
 * 환경변수 (.env):
 *   REACT_APP_FIREBASE_API_KEY
 *   REACT_APP_FIREBASE_AUTH_DOMAIN
 *   REACT_APP_FIREBASE_PROJECT_ID
 *   REACT_APP_FIREBASE_MESSAGING_SENDER_ID
 *   REACT_APP_FIREBASE_APP_ID
 *   REACT_APP_FIREBASE_VAPID_KEY
 */

import { getToken as getAuthToken } from './auth';

const isConfigured = !!(
  process.env.REACT_APP_FIREBASE_API_KEY &&
  process.env.REACT_APP_FIREBASE_PROJECT_ID &&
  process.env.REACT_APP_FIREBASE_APP_ID
);

/** FCM 토큰을 백엔드에 등록/갱신 */
async function registerTokenToServer(token: string, deviceType: 'web' | 'android' | 'ios'): Promise<void> {
  const authToken = getAuthToken();
  if (!authToken) return;
  try {
    const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000/api';
    await fetch(`${apiBase}/fcm/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ token, deviceType }),
    });
    console.log('[FCM] 토큰 서버 등록 완료:', deviceType);
  } catch (e) {
    console.warn('[FCM] 토큰 서버 등록 실패:', e);
  }
}

/** FCM 토큰을 백엔드에서 삭제 (로그아웃 시 호출) */
export async function unregisterFCMToken(token?: string): Promise<void> {
  const authToken = getAuthToken();
  if (!authToken) return;
  try {
    const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000/api';
    await fetch(`${apiBase}/fcm/token`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ token }),
    });
    console.log('[FCM] 토큰 삭제 완료');
  } catch (e) {
    console.warn('[FCM] 토큰 삭제 실패:', e);
  }
}

/**
 * 환경에 맞는 FCM 초기화 + 서버 토큰 등록 (로그인 후 호출)
 *
 * Firebase 환경변수가 설정되지 않은 경우 즉시 반환합니다.
 * 패키지 설치 후 아래 주석을 해제하여 활성화하세요.
 */
export async function initFCM(): Promise<void> {
  if (!isConfigured) {
    // Firebase 환경변수 미설정 — 정상 상태 (인앱 알림으로 대체 운용)
    return;
  }

  try {
    const isCapacitor = !!(window as any).Capacitor?.isNativePlatform?.();
    const platform = (window as any).Capacitor?.getPlatform?.() || 'web';
    let token: string | null = null;
    let deviceType: 'web' | 'android' | 'ios' = 'web';

    if (isCapacitor && (platform === 'android' || platform === 'ios')) {
      /**
       * 앱 환경 활성화:
       * npm install @capacitor-firebase/messaging 후 아래 주석 해제
       *
       * const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
       * const { receive } = await FirebaseMessaging.requestPermissions();
       * if (receive === 'granted') {
       *   const result = await FirebaseMessaging.getToken();
       *   token = result.token;
       *   await FirebaseMessaging.addListener('notificationReceived', (n: any) => {
       *     console.log('[FCM] 앱 알림:', n);
       *   });
       * }
       */
      deviceType = platform as 'android' | 'ios';
      console.log('[FCM] 앱 FCM: npm install @capacitor-firebase/messaging 필요');
    } else if ('Notification' in window && 'serviceWorker' in navigator) {
      /**
       * 웹 환경 활성화:
       * npm install firebase 후 아래 주석 해제
       *
       * const { initializeApp, getApps } = await import('firebase/app');
       * const { getMessaging, getToken, onMessage } = await import('firebase/messaging');
       * const config = {
       *   apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
       *   authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
       *   projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
       *   messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
       *   appId: process.env.REACT_APP_FIREBASE_APP_ID,
       * };
       * const app = getApps().length === 0 ? initializeApp(config) : getApps()[0];
       * const messaging = getMessaging(app);
       * const permission = await Notification.requestPermission();
       * if (permission === 'granted') {
       *   token = await getToken(messaging, {
       *     vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY
       *   });
       *   onMessage(messaging, (payload: any) => {
       *     if (payload.notification) {
       *       const { title = 'freematch', body = '' } = payload.notification;
       *       new Notification(title, { body, icon: '/favicon.ico' });
       *     }
       *   });
       * }
       */
      deviceType = 'web';
      console.log('[FCM] 웹 FCM: npm install firebase 필요');
    }

    if (token) {
      await registerTokenToServer(token, deviceType);
      localStorage.setItem('fcm_token', token);
    }
  } catch (e) {
    console.warn('[FCM] 초기화 중 오류:', e);
  }
}
